import { prop } from 'ramda';
import { useEffect, useRef } from 'react';

type TResizeMedia = 'PC' | 'mobile';

interface IResizeEffectTarget {
  /**
   * 화면 크기가 변할 때 호출할 콜백을 등록한다.
   * @param type `'PC'` 또는 `'mobile'`
   * @param callback 각 상황에 호출할 콜백
   */
  on: (type: TResizeMedia, callback: () => void) => IResizeEffectTarget;
}

interface IResizeEffectOptions {
  /** `true`면 최초 컴포넌트 마운트 시 화면 크기에 따라 콜백을 호출. */
  leading?: boolean;
  /**
   * PC 혹은 moblie 구분 미디어 쿼리
   * @default '(min-width: 48em)'
   */
  mediaQueryString?: string;
}

/**
 * 반응형 스크립트가 필요한 페이지를 위한 훅.
 * @example
 * const resizeEffect = useResizeEffect({ leading: true })
 *   .on('PC', hideHeader)
 *   .on('mobile', showHeader);
 * @see `IResizeEffectTarget`
 */
export const useResizeEffect = (options?: IResizeEffectOptions): IResizeEffectTarget => {
  const callbacks = useRef(new Map<TResizeMedia, Function[]>());

  useEffect(() => {
    const PCMediaQuery = window.matchMedia(options?.mediaQueryString ?? '(min-width: 48em)'); // default : 768px

    const fireCallbacks = (e: MediaQueryList | MediaQueryListEvent): void => {
      callbacks.current.get(e.matches ? 'PC' : 'mobile')?.forEach((fn) => fn());
    };
    PCMediaQuery.addListener(fireCallbacks);

    if (options && prop('leading', options)) {
      fireCallbacks(PCMediaQuery);
    }

    return () => PCMediaQuery.removeListener(fireCallbacks);
  }, []);

  return {
    on(type, callback) {
      const getType = callbacks.current.get(type);
      getType ? getType.push(callback) : callbacks.current.set(type, [callback]);

      return this;
    },
  };
};
